/**
 * Base class for Electron APIs - similar to NativeAPI for Tauri
 * Provides unified patterns for IPC communication, event handling, and cleanup
 */

import {
  type IPCEventTypeToInferredPayloadMap,
  ipcEventTypeToRequestPayloadMap,
} from '@electron/shared';
import { validateContextKitResponse } from '@electron/shared/utils';

import { listenWithAbortIPC } from '@/utils/abort';

import {
  type ElectronEventCallback,
  type ElectronListenerOptions,
  type ElectronUnsubscribe,
  type IPCEventType,
  type ContextKitResponse,
} from './types';

import type { InvokeChannel, PushChannel } from '@electron/shared/ipc-channels';

const log = (logType: `Electron-${string}`, ...args: unknown[]) => {
  // Only log in development - can be controlled by env var later
  if (process.env.NODE_ENV !== 'production') {
    console.info(logType, ...args);
  }
};

function isTimeoutOrAbortRejection(
  value: unknown
): value is { status: 'error'; reason: Error } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'status' in value &&
    value.status === 'error' &&
    'reason' in value &&
    value.reason instanceof Error
  );
}

/**
 * Base class for Electron APIs with event handling and IPC communication
 *
 * Provides:
 * - Unified event listener management with timeouts and abort signals
 * - IPC invoke methods for main process communication
 * - Observer lib communication methods
 * - Consistent error handling and cleanup
 */
export abstract class ElectronAPI {
  private unsubscribers: Map<string, ElectronUnsubscribe> = new Map();
  private timeouts: Map<string, NodeJS.Timeout> = new Map();
  private abortControllers: Map<string, AbortController> = new Map();

  /** Access to ipcRenderer */
  protected get ipcRenderer() {
    return window.ipcRenderer;
  }

  /** Access to main process API */
  protected get mainProcessApi() {
    return window.mainProcessApi;
  }

  /** Access to async ContextKit communication */
  protected get contextKitRequest() {
    return window.mainProcessApi.requestContextKit;
  }

  /** Access to sync ContextKit communication */
  protected get contextKitNotify() {
    return window.mainProcessApi.notifyContextKit;
  }

  /** Access to auth API */
  protected get authApi() {
    return window.authApi;
  }

  /** Check if Electron environment is available */
  protected get isElectronAvailable(): boolean {
    return !!(window.ipcRenderer && window.mainProcessApi);
  }

  /**
   * Add an event listener with unified options
   * Similar to NativeAPI.addListener but for Electron IPC events
   */
  protected addListener<T = unknown>(
    options: ElectronListenerOptions<T>
  ): ElectronUnsubscribe {
    const {
      eventType,
      callback,
      onError,
      onUnlisten,
      timeout,
      payloadValidator,
      signal,
      once,
    } = options;

    if (!this.isElectronAvailable) {
      console.warn('Electron environment not available');
      return () => {};
    }

    // Remove existing listener if any
    this.removeListener(eventType);

    log('Electron-listener added', eventType);

    // Create wrapped callback with validation and error handling
    const wrappedCallback: ElectronEventCallback<T> = (payload, callbackId) => {
      if (signal?.aborted) return;

      try {
        // Validate payload if validator is provided
        if (payloadValidator) {
          const validatedPayload = payloadValidator.parse(payload);
          callback(validatedPayload, callbackId);
        } else {
          callback(payload, callbackId);
        }

        log('Electron-Event Received', eventType, payload);

        // Remove listener if once flag is set
        if (once) {
          this.removeListener(eventType);
        }
      } catch (error) {
        console.error('Validation error for Electron event payload:', error);
        onError?.(error instanceof Error ? error : new Error(String(error)));
        this.removeListener(eventType);
      }
    };

    // Create abort signal for this listener
    const listenerAbortController = new AbortController();
    this.abortControllers.set(eventType, listenerAbortController);

    // Combine the provided signal with our listener signal
    const combinedSignal = signal
      ? this.createCombinedAbortSignal([signal, listenerAbortController.signal])
      : listenerAbortController.signal;

    const unsubscribe = listenWithAbortIPC(
      eventType,
      wrappedCallback as any, // Type assertion for generic callback compatibility
      combinedSignal
    );

    // Store the unsubscriber
    this.unsubscribers.set(eventType, () => {
      unsubscribe?.();
      onUnlisten?.();
    });

    // Set timeout if provided
    if (timeout !== undefined && timeout > 0) {
      const timeoutId = setTimeout(() => {
        log('Electron-Listener Timeout', eventType, timeout, 'ms');
        this.removeListener(eventType);
      }, timeout);
      this.timeouts.set(eventType, timeoutId);
    }

    // Handle AbortSignal
    if (signal) {
      signal.addEventListener('abort', () => {
        this.removeListener(eventType);
      });
    }

    return () => this.removeListener(eventType);
  }

  /**
   * Create a combined abort signal from multiple signals
   */
  private createCombinedAbortSignal(signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();

    signals.forEach((signal) => {
      if (signal.aborted) {
        controller.abort();
        return;
      }

      signal.addEventListener('abort', () => {
        controller.abort();
      });
    });

    return controller.signal;
  }

  /**
   * Remove a specific event listener
   */
  protected removeListener(eventType: PushChannel): void {
    if (!this.isElectronAvailable) return;

    const unsubscriber = this.unsubscribers.get(eventType);
    const timeoutId = this.timeouts.get(eventType);
    const abortController = this.abortControllers.get(eventType);

    if (unsubscriber) {
      unsubscriber();
      this.unsubscribers.delete(eventType);
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeouts.delete(eventType);
    }

    if (abortController) {
      abortController.abort();
      this.abortControllers.delete(eventType);
    }

    log('Electron-Listener Removed', eventType);
  }

  /**
   * Clear all event listeners for this API instance
   */
  public clearAllListeners(): void {
    if (!this.isElectronAvailable) return;

    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers.clear();

    this.timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeouts.clear();

    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();

    log('Electron-Event Listeners Cleared');
  }

  /**
   * Invoke IPC method on main process
   */
  protected async invoke<T>(method: InvokeChannel, args?: any): Promise<T> {
    if (!this.isElectronAvailable) {
      console.warn('Electron environment not available (invoke called)');
      return Promise.resolve(undefined as T);
    }

    log('Electron-IPC Invoked', method, args);
    return this.ipcRenderer.invoke(method, args);
  }

  /**
   * Send async message to ContextKit with result handling
   */
  protected async requestContextKit<
    TRequest extends IPCEventType,
    TResponse extends IPCEventType = TRequest,
  >(
    method: TRequest,
    args?: Record<string, unknown>,
    options?: {
      responseType?: TResponse;
      timeout?: number;
      signal?: AbortSignal;
    }
  ): Promise<IPCEventTypeToInferredPayloadMap[TResponse]> {
    if (!this.isElectronAvailable) {
      console.warn('Electron environment not available (ContextKit called)');
      return Promise.reject(new Error('Electron environment not available'));
    }

    log('Electron-ContextKit Invoked', method, args);

    const validatedArgs =
      ipcEventTypeToRequestPayloadMap[method]?.parse(args || {}) ??
      (args || {});

    const response = await (
      options?.timeout !== undefined || options?.signal !== undefined
        ? this.contextKitRequest(
            method,
            validatedArgs,
            options.timeout,
            options.signal
          )
        : this.contextKitRequest(method, validatedArgs)
    ).catch((rejection: unknown) => {
      throw isTimeoutOrAbortRejection(rejection) ? rejection.reason : rejection;
    });
    const responseType = (options?.responseType ?? method) as TResponse;

    // Handle the raw response from MainProcessApi
    if (response && typeof response === 'object' && 'status' in response) {
      const observerResponse = response as ContextKitResponse<TResponse>;
      if (observerResponse.status === 'error') {
        throw (
          observerResponse.reason || new Error(`ContextKit error for ${method}`)
        );
      }
      return validateContextKitResponse(responseType, observerResponse.data);
    }

    // If response is not wrapped, assume it's the direct data
    return validateContextKitResponse(responseType, response);
  }

  /**
   * Send sync message to ContextKit (fire and forget)
   */
  protected notifyContextKit(
    method: IPCEventType,
    args?: Record<string, unknown>
  ): void {
    if (!this.isElectronAvailable) {
      console.warn(
        'Electron environment not available (sync ContextKit called)'
      );
      return;
    }

    log('Electron-ContextKit Sync', method, args);
    this.contextKitNotify(method, args || {});
  }
}
